import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { EuConfirmDialog, EuInlineNotice, Spinner } from '@newamsterdam/ui'
import { useUser } from '../store/user'
import { CompanyChooser } from './CompanyChooser'
import { companyErrorMessage } from './companyError'
import styles from './CompanyGate.module.css'

export function CompanyGate({ children }: { children: ReactNode }) {
  const id = useUser((s) => s.id)
  const companies = useUser((s) => s.companies)
  const currentCompanyId = useUser((s) => s.current_company_id)
  const defaultCompanyId = useUser((s) => s.default_company_id)
  const selectCompany = useUser((s) => s.selectCompany)
  const logout = useUser((s) => s.logout)

  const needsPick = !!id && !currentCompanyId && companies.length > 1
  const needsAutoSelect = !!id && !currentCompanyId && companies.length === 1

  const suggested = useMemo(() => {
    if (defaultCompanyId && companies.some((c) => c.id === defaultCompanyId)) {
      return defaultCompanyId
    }
    return companies[0]?.id ?? ''
  }, [defaultCompanyId, companies])

  const [choice, setChoice] = useState<string>(suggested)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [suggestedSeen, setSuggestedSeen] = useState(suggested)

  if (suggested !== suggestedSeen) {
    setSuggestedSeen(suggested)
    setChoice(suggested)
  }

  useEffect(() => {
    if (!needsAutoSelect) return
    const only = companies[0]
    if (!only) return
    let cancelled = false
    void (async () => {
      try {
        await selectCompany(only.id)
      } catch (err) {
        if (cancelled) return
        setError(companyErrorMessage(err))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [needsAutoSelect, companies, selectCompany])

  if (!needsPick && !needsAutoSelect) return <>{children}</>

  if (needsAutoSelect) {
    return (
      <div className={styles.wrap}>
        {error ? (
          <div className={styles.notice}>
            <EuInlineNotice tone="error">{error}</EuInlineNotice>
          </div>
        ) : (
          <Spinner />
        )}
      </div>
    )
  }

  async function handleConfirm() {
    if (submitting || !choice) return
    setError(null)
    setSubmitting(true)
    try {
      await selectCompany(choice)
    } catch (err) {
      setError(companyErrorMessage(err))
      setSubmitting(false)
    }
  }

  return (
    <EuConfirmDialog
      open
      title="Wybierz spółkę"
      message="Wybierz spółkę, w kontekście której chcesz pracować."
      details={
        <CompanyChooser
          companies={companies}
          value={choice}
          onChange={setChoice}
          error={error}
          disabled={submitting}
        />
      }
      confirmLabel={submitting ? 'Kontynuuję...' : 'Kontynuuj'}
      cancelLabel="Wyloguj"
      tone="default"
      onConfirm={() => void handleConfirm()}
      onCancel={() => void logout()}
    />
  )
}
